import { useCallback } from 'react'
import type { MessageKey } from '@/i18n'
import type { RefusalReason, RefusedScan, TagEntry } from '../commands'
import { checkCode, expandTypedCode, type GuardContext } from '../model/scanGuard'
import { tr } from './text'

const REFUSAL: Record<RefusalReason, MessageKey> = {
  TAG_UNKNOWN: 'gate.v1.refuse.unknown',
  TAG_WRONG_KIND: 'gate.refuse.wrongKind',
  ROLL_NOT_CONFIRMED: 'gate.refuse.notConfirmed',
  TAG_VOID: 'gate.refuse.void',
  TAG_ALREADY_USED: 'gate.refuse.used',
  TAG_DUPLICATE_SCAN: 'gate.refuse.duplicate',
}

export interface ScanCheck {
  guard: GuardContext
  onAccept: (payload: string, entry: TagEntry) => void
  onRefuse?: (scan: RefusedScan) => void
}

/**
 * One code through the tag guard (village rolls in V1). A typed number is expanded on the
 * village's rolls ("2204" → its full code). Refusals are logged (F4.6-c) and come back as words.
 */
export function useCheckedScan({ guard, onAccept, onRefuse }: ScanCheck) {
  return useCallback(
    (raw: string, entry: TagEntry): string | null => {
      const live = guard.rolls.filter((r) => guard.confirmedBatchIds.has(r.batch_id))
      const code = entry === 'TYPED' ? expandTypedCode(raw, guard.kind, live) : raw.trim()
      const res = checkCode(code, guard)
      if (!res.ok) {
        onRefuse?.({
          payload: res.payload,
          reason: res.reason,
          occurred_at: new Date().toISOString(),
        })
        return res.otherFarmer !== undefined
          ? tr('gate.v1.refuse.otherFarmer', { name: res.otherFarmer })
          : tr(REFUSAL[res.reason])
      }
      onAccept(res.payload, entry)
      return null
    },
    [guard, onAccept, onRefuse],
  )
}
